import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "urql"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { sendDynamicMessageSchema, type SendDynamicMessageFormValues } from "@/lib/schemas"
import { SendDynamicMessageMutation } from "@/lib/graphql/outbound"

interface SendDynamicMessageFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function SendDynamicMessageForm({ open, onOpenChange, onSuccess }: SendDynamicMessageFormProps) {
  const [, sendDynamic] = useMutation(SendDynamicMessageMutation)

  const form = useForm<SendDynamicMessageFormValues>({
    resolver: zodResolver(sendDynamicMessageSchema) as Resolver<SendDynamicMessageFormValues>,
    defaultValues: {
      url: "",
      eventType: "",
      payload: "",
      headers: "",
      signingSecret: "",
      timeoutMs: "" as unknown as number,
      rateLimitRps: "" as unknown as number,
      maxAttempts: "" as unknown as number,
      initialDelay: "",
      maxDelay: "",
      backoffFactor: "" as unknown as number,
      idempotencyKey: "",
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        url: "",
        eventType: "",
        payload: "",
        headers: "",
        signingSecret: "",
        timeoutMs: "" as unknown as number,
        rateLimitRps: "" as unknown as number,
        maxAttempts: "" as unknown as number,
        initialDelay: "",
        maxDelay: "",
        backoffFactor: "" as unknown as number,
        idempotencyKey: "",
      })
    }
  }, [open, form])

  const onSubmit = async (values: SendDynamicMessageFormValues) => {
    let payload: string
    try {
      JSON.parse(values.payload)
      payload = btoa(values.payload)
    } catch {
      form.setError("payload", { message: "Invalid JSON" })
      return
    }

    let headers: Record<string, string> | undefined
    if (values.headers?.trim()) {
      try {
        headers = JSON.parse(values.headers)
      } catch {
        form.setError("headers", { message: "Invalid JSON" })
        return
      }
    }

    const hasRetry = values.maxAttempts || values.initialDelay || values.maxDelay || values.backoffFactor
    const retryPolicy = hasRetry
      ? {
          maxAttempts: Number(values.maxAttempts) || 5,
          initialDelay: values.initialDelay || "5s",
          maxDelay: values.maxDelay || "1h",
          backoffFactor: Number(values.backoffFactor) || 2,
        }
      : undefined

    const result = await sendDynamic({
      input: {
        url: values.url,
        eventType: values.eventType,
        payload,
        headers: headers ?? undefined,
        signingSecret: values.signingSecret || undefined,
        retryPolicy,
        timeoutMs: values.timeoutMs || undefined,
        rateLimitRps: values.rateLimitRps || undefined,
        idempotencyKey: values.idempotencyKey || undefined,
      },
    })

    if (result.error) {
      toast.error(result.error.message)
      return
    }

    toast.success("Dynamic message sent")
    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send Dynamic Message</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Send a one-off webhook to any URL without creating an application or endpoint.
          </p>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="dm-url">Destination URL</Label>
              <Input
                id="dm-url"
                className="font-mono text-sm"
                placeholder="https://example.com/webhook"
                {...form.register("url")}
              />
              <FieldError error={form.formState.errors.url} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dm-eventType">Event Type</Label>
              <Input
                id="dm-eventType"
                className="font-mono text-sm"
                placeholder="invoice.paid"
                {...form.register("eventType")}
              />
              <FieldError error={form.formState.errors.eventType} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dm-payload">Payload (JSON)</Label>
              <Textarea
                id="dm-payload"
                className="font-mono text-xs min-h-25"
                placeholder='{"key": "value"}'
                {...form.register("payload")}
              />
              <FieldError error={form.formState.errors.payload} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dm-headers">
                Headers (JSON) <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Textarea
                id="dm-headers"
                className="font-mono text-xs min-h-15"
                placeholder='{"Authorization": "Bearer token"}'
                {...form.register("headers")}
              />
              <FieldError error={form.formState.errors.headers} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dm-signingSecret">
                Signing Secret <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="dm-signingSecret"
                className="font-mono text-sm"
                placeholder="whsec_..."
                {...form.register("signingSecret")}
              />
            </div>
          </div>

          <div className="space-y-4 rounded-lg glass-card p-4">
            <h4 className="text-sm font-medium">Delivery Options</h4>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="dm-timeoutMs">
                  Timeout (ms) <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="dm-timeoutMs"
                  type="number"
                  className="font-mono text-sm"
                  placeholder="30000"
                  {...form.register("timeoutMs")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dm-rateLimitRps">
                  Rate Limit (RPS) <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="dm-rateLimitRps"
                  type="number"
                  className="font-mono text-sm"
                  placeholder="100"
                  {...form.register("rateLimitRps")}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-lg glass-card p-4">
            <h4 className="text-sm font-medium">
              Retry Policy <span className="text-muted-foreground font-normal">(optional)</span>
            </h4>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="dm-maxAttempts">Max Attempts</Label>
                <Input
                  id="dm-maxAttempts"
                  type="number"
                  className="font-mono text-sm"
                  placeholder="5"
                  {...form.register("maxAttempts")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dm-backoffFactor">Backoff Factor</Label>
                <Input
                  id="dm-backoffFactor"
                  type="number"
                  step="0.1"
                  className="font-mono text-sm"
                  placeholder="2"
                  {...form.register("backoffFactor")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dm-initialDelay">Initial Delay</Label>
                <Input
                  id="dm-initialDelay"
                  className="font-mono text-sm"
                  placeholder="5s"
                  {...form.register("initialDelay")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dm-maxDelay">Max Delay</Label>
                <Input
                  id="dm-maxDelay"
                  className="font-mono text-sm"
                  placeholder="1h"
                  {...form.register("maxDelay")}
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="dm-idempotencyKey">
                Idempotency Key <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="dm-idempotencyKey"
                className="font-mono text-sm"
                placeholder="Auto-generated if empty"
                {...form.register("idempotencyKey")}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : "Send"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
